<template>
  <div class="w-full">
    <!-- Label -->
    <BaseText
      v-if="label"
      type="label"
      size="md"
      class="text-white w-full mb-1"
    >
      {{ label }}
    </BaseText>

    <!-- Input Container -->
    <div
      class="input-container w-full flex items-center"
      :class="{
        'active': value?.length || isFocused,
        'errored': error,
        'disabled': disabled
      }"
    >
      <input
        :value="value"
        class="form-input flex-grow min-w-0"
        :placeholder="placeholder"
        :type="type"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        @focus="handleFocus"
        @blur="handleBlur"
        @input="handleInput"
      >

      <!-- Icon Slot -->
      <transition>
        <component
          :is="iconComponent"
          v-if="showIcon"
          :style="{ color: iconColor }"
          class="ml-2 flex-shrink-0"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import BaseText from './BaseText.vue'
import InfoIcon from './Icons/InfoSimpleIcon.vue'

export default {
  name: 'BaseInputDark',

  components: {
    BaseText
  },

  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    error: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    icon: {
      type: [String, Object],
      default: null
    },
    iconColor: {
      type: String,
      default: '#DF1C41'
    },
    showIconOnError: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      isFocused: false
    }
  },

  computed: {
    showIcon () {
      if (this.showIconOnError && this.error) {
        return true
      }
      return this.icon && !this.error
    },

    iconComponent () {
      if (this.showIconOnError && this.error) {
        return InfoIcon // Default error icon
      }
      return this.icon
    }
  },

  methods: {
    handleFocus (event) {
      if (this.disabled || this.readonly) return
      this.isFocused = true
      this.$emit('focus', event)
    },

    handleBlur (event) {
      this.isFocused = false
      this.$emit('blur', event)
    },

    handleInput (event) {
      this.$emit('input', event.target.value)
    }
  }
}
</script>

<style scoped>
.input-container {
  background-color: rgba(255, 255, 255, 0.10);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.10);
  border-radius: 10px;
  padding: 8px 14px;
  display: flex;
  align-items: center;
  transition: background-color 150ms ease-in-out, box-shadow 150ms ease-in-out;
}

.input-container:hover:not(.disabled) {
  background-color: rgba(255, 255, 255, 0.16);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.16);
}

.input-container.active:not(.disabled),
.input-container.active:hover:not(.disabled) {
  background-color: transparent;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.10);
}

.input-container.errored:not(.disabled),
.input-container.active.errored:not(.disabled),
.input-container.active.errored:hover:not(.disabled) {
  background-color: transparent;
  box-shadow: 0 0 0 1px #F4003A;
}

.input-container.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: rgba(255, 255, 255, 0.05);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05);
}

.form-input {
  background-color: transparent;
  color: white;
  caret-color: white;
  max-width: none;
  font-weight: 300;
  font-size: 16px;
  line-height: 24px;

  /* Reset default styles */
  -webkit-appearance: none;  /* Remove default styling in WebKit browsers */
  -moz-appearance: none;     /* Remove default styling in Firefox */
  appearance: none;          /* Remove default styling in modern browsers */
  padding: 0;
  margin: 0;
  border: none;
  font-size: inherit;
  outline: none;

  /* This is because browsers want to force their ugly styling on you when autofill is triggered. */
  /* This is the only way to prevent that, since they use !important in their overrides. */
  transition:
    color 99999999s step-end,
    background-color 99999999s step-end;
}

.form-input:disabled {
  cursor: not-allowed;
}

.form-input:focus {
  outline: none;
  border: none;
  box-shadow: none;
}

.form-input::placeholder {
  transition: color 200ms ease-in-out;
  color: rgba(255, 255, 255, 0.44);
}

.form-input:focus::placeholder {
  color: rgba(255, 255, 255, 0.2);
}

/* Override browser autofill styles */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
textarea:-webkit-autofill,
textarea:-webkit-autofill:hover,
textarea:-webkit-autofill:focus,
select:-webkit-autofill,
select:-webkit-autofill:hover,
select:-webkit-autofill:focus {
  -webkit-text-fill-color: white !important;
}

/* Firefox autocomplete */
.form-input:-moz-autofill {
  color: white !important;
}

/* General autocomplete prevention */
.form-input:autofill {
  color: white !important;
}

/* Transition for icon */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.2s ease;
}

.v-enter,
.v-leave-to {
  opacity: 0;
}
</style>

